import { type DefaultRootState, useActionWithDependencies } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { type DatabaseField, type DatabaseValue } from '@databases/types/database';
import { loadDependencies } from '@databases/utils/database/field-types';
import {
  getConvertedDatabaseValue,
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  isBrokenDatabase,
} from '@databases/utils/database/utils';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import { deserializeError } from '@databases/utils/utils';
import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

// Memoize selectDatabase function by scope to enable caching in getBacklinkEntries()
const selectDatabaseDraft: Record<string, typeof selectDatabaseByContentIdOptional> = {};
export function getSelectDatabaseDraft(
  includeDeleted: boolean
): typeof selectDatabaseByContentIdOptional {
  const key = `main-${includeDeleted}`;
  if (!selectDatabaseDraft[key]) {
    selectDatabaseDraft[key] = (state, { contentId }) => {
      // Check if target database is present, return undefined in case it doesn't rather than throwing an exception
      const targetDatabase = selectDatabaseByContentIdOptional(state, { contentId });
      return targetDatabase && !isBrokenDatabase(targetDatabase)
        ? selectDatabaseDraftByContentId(state as DefaultRootState, {
            contentId: targetDatabase.contentId,
            includeDeleted,
          })
        : undefined;
    };
  }
  return selectDatabaseDraft[key];
}

export function useLoadDependencies(
  context:
    | {
        contentId: string;
        entryId?: string;
        field: DatabaseField;
        convertedValue: DatabaseValue;
      }
    | { contentId: string; entryId: string; fieldId: string },
  state: DefaultRootState | undefined
): void {
  const intl = useIntl();
  useActionWithDependencies(
    useCallback(
      (helpers) => {
        const selectDatabase = getSelectDatabaseDraft(true);
        const { contentId, entryId } = context;
        const { field, convertedValue } =
          'convertedValue' in context
            ? context
            : helpers.selector((state) => {
                const database = selectDatabase(state, { contentId });
                if (!database) {
                  throw new Error(`Database ${contentId} not loaded.`);
                } else if (isBrokenDatabase(database)) {
                  throw deserializeError(database.error);
                }
                const field = getDatabaseFieldRequired(database, context.fieldId);
                const entry = getDatabaseEntryRequired(database, entryId!);
                const convertedValue = getConvertedDatabaseValue(
                  database,
                  field,
                  entry,
                  state
                );
                return { field, convertedValue };
              });
        loadDependencies(
          { contentId, field, entryId, value: convertedValue },
          { ...helpers, selectDatabase, intl },
          state
        );
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [...Object.values(context)]
    )
  );
}
